import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Linkedin, MapPin, Mail, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export function Footer() {
  const { t, language } = useLanguage();
  const isRu = language === 'ru';

  return (
    <footer className="bg-navy-950 text-white/80 pt-20 pb-8 border-t border-white/10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-6">
              <img 
                src="https://vibe.filesafe.space/1777392978847194987/attachments/7e47838b-fe62-45a6-9466-14b47e07dd0c.webp" 
                srcSet="https://vibe.filesafe.space/1777392978847194987/attachments/7e47838b-fe62-45a6-9466-14b47e07dd0c.webp?w=150 150w, https://vibe.filesafe.space/1777392978847194987/attachments/7e47838b-fe62-45a6-9466-14b47e07dd0c.webp?w=300 300w"
                sizes="150px"
                crossOrigin="anonymous" 
                alt="AITIM Group Logo" 
                width={150} 
                height={48} 
                loading="lazy"
                className="h-16 w-auto object-contain" 
              />
            </Link>
            <p className="text-sm mb-6 max-w-xs">
              {t('footer.desc')}
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/aitiminc/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors">
                <Facebook className="w-5 h-5 text-white" />
              </a>
              <a href="https://www.instagram.com/aitim_group/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors">
                <Instagram className="w-5 h-5 text-white" />
              </a>
              <a href="https://t.me/aitim_sales" target="_blank" rel="noopener noreferrer" aria-label="Telegram" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.19-.08-.05-.19-.02-.27 0-.12.03-1.98 1.26-5.6 3.71-.53.36-1.01.53-1.44.52-.47-.01-1.38-.27-2.05-.49-.83-.27-1.49-.41-1.43-.87.03-.24.36-.49.99-.75 3.88-1.69 6.47-2.8 7.76-3.34 3.69-1.54 4.46-1.81 4.96-1.82.11 0 .36.03.52.16.14.12.18.28.2.42.02.11.02.24.01.38z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/aitim" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors">
                <Linkedin className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>

          <div>
            <h2 className="text-white font-semibold mb-6 uppercase text-sm tracking-wider">{t('footer.services')}</h2>
            <ul className="space-y-3 text-sm">
              <li><Link to="/safety-compliance" className="hover:text-primary transition-colors">Safety & Compliance</Link></li>
              <li><Link to="/consulting" className="hover:text-primary transition-colors">Consulting</Link></li>
              <li><Link to="/truck-insurance" className="hover:text-primary transition-colors">Insurance</Link></li>
              <li><Link to="/fuel-cards" className="hover:text-primary transition-colors">Fuel Cards</Link></li>
              <li><Link to="/factoring" className="hover:text-primary transition-colors">Factoring & Accounting</Link></li>
              <li><Link to="/hr-recruiting" className="hover:text-primary transition-colors">HR & Recruiting</Link></li>
              <li><Link to="/tms" className="hover:text-primary transition-colors">TMS Software</Link></li>
              <li><Link to="/permitsandlicensing" className="hover:text-primary transition-colors">Permits & Licensing</Link></li>
              <li><Link to="/brokerage" className="hover:text-primary transition-colors">Brokerage</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="text-white font-semibold mb-6 uppercase text-sm tracking-wider">{t('footer.consulting')}</h2>
            <ul className="space-y-3 text-sm">
              <li><Link to="/free-consultation" className="hover:text-primary transition-colors">Business Consulting</Link></li>
              <li><Link to="/business-services" className="hover:text-primary transition-colors">Business Services</Link></li>
              <li><Link to="/free-consultation" className="hover:text-primary transition-colors">Operations Support</Link></li>
              <li><Link to="/free-consultation" className="hover:text-primary transition-colors">Back-Office Support</Link></li>
              <li><Link to="/free-consultation" className="hover:text-primary transition-colors">Compliance Support</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="text-white font-semibold mb-6 uppercase text-sm tracking-wider">{t('footer.company')}</h2>
            <ul className="space-y-3 text-sm mb-8">
              <li><Link to="/about" className="hover:text-primary transition-colors">About Us</Link></li>
              <li><Link to="/free-consultation" className="hover:text-primary transition-colors">Contact Us</Link></li>
              <li><Link to="/job-application" className="hover:text-primary transition-colors">Careers</Link></li>

            </ul>

          </div>

          <div>
            <h2 className="text-white font-semibold mb-6 uppercase text-sm tracking-wider">{t('footer.contact')}</h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>773-980-9090</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>info@aitimgroup.com</span>
              </li>

            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between text-xs text-white/50">
          <p>© 2026 AITIM Group, Inc. {t('footer.rights')}</p>
          <div className="flex items-center gap-6 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">{t('footer.privacy')}</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">{t('footer.terms')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
